import './style.scss';

import { __experimentalNumberControl as NumberControl } from '@wordpress/components';
import { isRTL } from '@wordpress/i18n';
import classNames from 'classnames';
import React, {
	forwardRef,
	useCallback,
	useMemo,
	useRef,
	useState,
} from 'react';

import type {
	InputRangeProps,
	RangeControlProps,
	RangeMark,
	RangeMarkProps,
	RangeRailProps,
	TooltipProps,
	UseControlledRangeValueArgs,
} from './types';

const noop = () => {};

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Clamp a value between min and max
 */
function clamp(value: number, min: number, max: number): number {
	return Math.min(Math.max(value, min), max);
}

/**
 * A float supported clamp function for a specific value.
 */
function floatClamp(
	value: number | null,
	min: number,
	max: number,
): number | null {
	if (typeof value !== 'number') {
		return null;
	}
	return parseFloat(`${clamp(value, min, max)}`);
}

/**
 * Hook to store a clamped value, derived from props.
 */
function useControlledRangeValue(settings: UseControlledRangeValueArgs) {
	const { min, max, value: valueProp, initial } = settings;

	// Calculate initial state
	const initialValue = useMemo(() => {
		if (valueProp !== null && valueProp !== undefined) {
			return floatClamp(valueProp, min, max);
		}
		if (initial !== undefined) {
			return floatClamp(initial, min, max);
		}
		return null;
	}, []);

	const [internalState, setInternalState] = useState<number | null>(
		initialValue,
	);

	// Determine if we're controlled or uncontrolled
	const isControlled = valueProp !== undefined;
	const state = isControlled ? floatClamp(valueProp, min, max) : internalState;

	const setState = useCallback(
		(nextValue: number | null) => {
			if (nextValue === null) {
				setInternalState(null);
			} else {
				setInternalState(floatClamp(nextValue, min, max));
			}
		},
		[min, max],
	);

	return [state, setState] as const;
}

/**
 * Generate marks based on step
 */
function useMarks(
	marks: boolean | RangeMark[] | undefined,
	min: number,
	max: number,
	step: number | 'any',
): RangeMark[] {
	return useMemo(() => {
		if (!marks) {
			return [];
		}

		if (Array.isArray(marks)) {
			return marks;
		}

		// Auto-generate marks based on step
		if (step === 'any') {
			return [];
		}

		const generatedMarks: RangeMark[] = [];
		for (let i = min; i <= max; i += step) {
			generatedMarks.push({ value: i });
		}
		return generatedMarks;
	}, [marks, min, max, step]);
}

// =============================================================================
// SUB-COMPONENTS
// =============================================================================

/**
 * InputRange - The actual range input element
 */
const InputRange = forwardRef<HTMLInputElement, InputRangeProps>(
	(props, ref) => {
		const {
			min,
			max,
			step,
			value,
			disabled,
			onChange,
			onBlur,
			onFocus,
			onMouseMove,
			onMouseLeave,
			id,
			'aria-label': ariaLabel,
			'aria-describedby': ariaDescribedBy,
		} = props;

		return (
			<input
				ref={ref}
				type="range"
				className="redfox-thank-you-range-control__slider"
				min={min}
				max={max}
				step={step}
				value={value}
				disabled={disabled}
				onChange={onChange}
				onBlur={onBlur}
				onFocus={onFocus}
				onMouseMove={onMouseMove}
				onMouseLeave={onMouseLeave}
				id={id}
				aria-label={ariaLabel}
				aria-describedby={ariaDescribedBy}
				aria-hidden={false}
				tabIndex={0}
			/>
		);
	},
);

InputRange.displayName = 'InputRange';

/**
 * Mark - Single mark indicator
 */
const Mark: React.FC<RangeMarkProps & { style?: React.CSSProperties }> = ({
	isFilled,
	label,
	disabled,
	style,
}) => {
	return (
		<span
			className={classNames('redfox-thank-you-range-control__mark', {
				'is-filled': isFilled,
				'is-disabled': disabled,
			})}
			style={style}
		>
			{label && (
				<span
					className={classNames(
						'redfox-thank-you-range-control__mark-label',
						{
							'is-filled': isFilled,
						},
					)}
				>
					{label}
				</span>
			)}
		</span>
	);
};

/**
 * RangeRail - The rail with marks
 */
const RangeRail: React.FC<RangeRailProps> = ({
	min,
	max,
	step,
	value,
	marks,
	disabled,
	railColor,
}) => {
	const marksData = useMarks(marks, min, max, step === 'any' ? 1 : step);

	const railStyle: React.CSSProperties = railColor
		? { backgroundColor: railColor }
		: {};

	if (disabled) {
		railStyle.backgroundColor = '#a0a5aa';
	}

	return (
		<span
			className="redfox-thank-you-range-control__rail"
			style={railStyle}
			aria-hidden
		>
			{marksData.length > 0 && (
				<span className="redfox-thank-you-range-control__marks">
					{marksData.map((mark, index) => {
						const position =
							((mark.value - min) / (max - min)) * 100;
						const isFilled =
							value !== null && mark.value <= value;

						return (
							<Mark
								key={`${mark.value}-${index}`}
								isFilled={isFilled}
								label={mark.label}
								disabled={disabled}
								style={{
									[isRTL() ? 'right' : 'left']: `${position}%`,
								}}
							/>
						);
					})}
				</span>
			)}
		</span>
	);
};

/**
 * SimpleTooltip - Tooltip showing current value
 */
const SimpleTooltip: React.FC<TooltipProps> = ({
	show,
	value,
	position = 'bottom',
	renderTooltipContent = (v) => v,
	style,
}) => {
	if (!show) {
		return null;
	}

	const content = renderTooltipContent(value);

	return (
		<span
			className={classNames('redfox-thank-you-range-control__tooltip', {
				'is-bottom': position === 'bottom',
				'is-top': position === 'top',
			})}
			style={style}
		>
			{content}
		</span>
	);
};

// =============================================================================
// MAIN COMPONENT
// =============================================================================

/**
 * RangeControl - A range slider component for selecting numeric values
 *
 * This is a simplified version without label/description support,
 * designed to be used with BaseControl for consistent styling.
 *
 * @example
 * ```tsx
 * <BaseControl label="Opacity">
 *   <RangeControl
 *     value={opacity}
 *     onChange={setOpacity}
 *     min={0}
 *     max={100}
 *   />
 * </BaseControl>
 * ```
 */
function UnforwardedRangeControl(
	props: RangeControlProps,
	forwardedRef: React.ForwardedRef<HTMLInputElement>,
) {
	const {
		min = 0,
		max = 100,
		value: valueProp,
		step = 1,
		disabled = false,
		marks = false,
		initialPosition,
		trackColor,
		railColor,
		showTooltip: showTooltipProp,
		renderTooltipContent = (v) => v,
		withInputField = true,
		isShiftStepEnabled = true,
		shiftStep = 10,
		onChange = noop,
		onBlur = noop,
		onFocus = noop,
		onMouseMove = noop,
		onMouseLeave = noop,
		className,
		id,
		'aria-label': ariaLabel,
		'aria-describedby': ariaDescribedBy,
	} = props;

	const [value, setValue] = useControlledRangeValue({
		min,
		max,
		value: valueProp ?? null,
		initial: initialPosition,
	});

	const isResetPending = useRef(false);

	// Determine if tooltip should show
	let hasTooltip = showTooltipProp;
	let hasInputField = withInputField;

	if (step === 'any') {
		// Hide tooltip and number input when step is "any"
		hasTooltip = false;
		hasInputField = false;
	}

	const [showTooltip, setShowTooltip] = useState(hasTooltip);
	const [isFocused, setIsFocused] = useState(false);

	const inputRef = useRef<HTMLInputElement>(null);
	const isCurrentlyFocused = inputRef.current?.matches(':focus');
	const isThumbFocused = !disabled && isFocused;

	const isValueReset = value === null;
	const inputSliderValue = isValueReset ? '' : value;
	const rangeFillValue = isValueReset ? (max - min) / 2 + min : value;

	// Calculate fill percentage
	const fillValue = isValueReset
		? 50
		: ((value - min) / (max - min)) * 100;
	const fillValueOffset = `${clamp(fillValue, 0, 100)}%`;

	const enableTooltip = hasTooltip !== false && Number.isFinite(value);

	// Event handlers
	const handleOnRangeChange = (
		event: React.ChangeEvent<HTMLInputElement>,
	) => {
		const nextValue = parseFloat(event.target.value);
		setValue(nextValue);
		onChange(nextValue);
	};

	const handleOnChange = (next?: string) => {
		let nextValue = parseFloat(next ?? '');
		setValue(nextValue);

		if (!isNaN(nextValue)) {
			if (nextValue < min || nextValue > max) {
				nextValue = floatClamp(nextValue, min, max) as number;
			}
			onChange(nextValue);
			isResetPending.current = false;
		} else {
			isResetPending.current = true;
		}
	};

	const handleOnInputNumberBlur = () => {
		if (isResetPending.current) {
			// Reset to initial position or null
			const resetValue =
				initialPosition !== undefined ? initialPosition : null;
			setValue(resetValue);
			onChange(resetValue ?? undefined);
			isResetPending.current = false;
		}
	};

	const handleShowTooltip = () => setShowTooltip(true);
	const handleHideTooltip = () => setShowTooltip(false);

	const handleOnBlur = (event: React.FocusEvent<HTMLInputElement>) => {
		onBlur(event);
		setIsFocused(false);
		handleHideTooltip();
	};

	const handleOnFocus = (event: React.FocusEvent<HTMLInputElement>) => {
		onFocus(event);
		setIsFocused(true);
		handleShowTooltip();
	};

	const offsetStyle: React.CSSProperties = {
		[isRTL() ? 'right' : 'left']: fillValueOffset,
	};

	const trackStyle: React.CSSProperties = {
		width: fillValueOffset,
	};

	if (trackColor) {
		trackStyle.backgroundColor = trackColor;
	}

	if (disabled) {
		trackStyle.backgroundColor = '#949494';
	}

	const classes = classNames('redfox-thank-you-range-control', className, {
		'is-disabled': disabled,
		'is-marked': !!marks,
	});

	// Merge refs
	const mergedRef = useCallback(
		(node: HTMLInputElement | null) => {
			(inputRef as React.MutableRefObject<HTMLInputElement | null>).current =
				node;
			if (typeof forwardedRef === 'function') {
				forwardedRef(node);
			} else if (forwardedRef) {
				forwardedRef.current = node;
			}
		},
		[forwardedRef],
	);

	return (
		<div className={classes}>
			<div className="redfox-thank-you-range-control__wrapper">
				<InputRange
					ref={mergedRef}
					min={min}
					max={max}
					step={step}
					value={inputSliderValue ?? ''}
					disabled={disabled}
					onChange={handleOnRangeChange}
					onBlur={handleOnBlur}
					onFocus={handleOnFocus}
					onMouseMove={onMouseMove}
					onMouseLeave={onMouseLeave}
					id={id}
					aria-label={ariaLabel}
					aria-describedby={ariaDescribedBy}
				/>

				<RangeRail
					min={min}
					max={max}
					step={step}
					value={rangeFillValue}
					marks={marks}
					disabled={disabled}
					railColor={railColor}
				/>

				<span
					className="redfox-thank-you-range-control__track"
					style={trackStyle}
					aria-hidden
				/>

				<span
					className={classNames(
						'redfox-thank-you-range-control__thumb-wrapper',
						{
							'is-disabled': disabled,
						},
					)}
					style={offsetStyle}
				>
					<span
						className={classNames(
							'redfox-thank-you-range-control__thumb',
							{
								'is-focused': isThumbFocused,
								'is-disabled': disabled,
							},
						)}
						aria-hidden
					/>
				</span>

				{enableTooltip && (
					<SimpleTooltip
						show={isCurrentlyFocused || showTooltip}
						value={value}
						position="bottom"
						renderTooltipContent={renderTooltipContent}
						style={offsetStyle}
					/>
				)}
			</div>

			{hasInputField && (
				<NumberControl
					className="redfox-thank-you-range-control__number"
					disabled={disabled}
					min={min}
					max={max}
					step={step === 'any' ? 1 : step}
					value={inputSliderValue ?? ''}
					onChange={handleOnChange}
					onBlur={handleOnInputNumberBlur}
					shiftStep={shiftStep}
					isShiftStepEnabled={isShiftStepEnabled}
					size="__unstable-large"
					__unstableInputWidth="60px"
					spinControls="none"
					aria-label={ariaLabel}
				/>
			)}
		</div>
	);
}

/**
 * RangeControl component for selecting numeric values within a range.
 *
 * Use with BaseControl for label and description support.
 */
export const RangeControl = forwardRef(UnforwardedRangeControl);
